<script lang="ts">
export default {
  name: 'KKRoleSelect',
}
</script>

<script setup lang="ts">
import {KkBox, KkFormLabel, KkRadioGroup, KkLoading} from '../../../../components/base'
import {useI18n} from 'vue-i18n'
import {useStore} from 'vuex'
import SelectStore from '../../../../components/SelectStore.vue'
import {KkInfo} from '../../../../components/ui'
import {computed, ref} from 'vue'
import FilterModal from '../../../../components/CompaniesRoleManagements/FilterModal.vue'
import StoreModal from '../../../../components/CompaniesRoleManagements/StoreModal.vue'
import {roleConst} from '../../../../modules/roleConst'

// 型定義作成
type SelectType = 'all' | 'select'
type Role = typeof roleConst[keyof typeof roleConst]
type StoreUuids = string[]
type Props = {
  modelValue: Role
  storeUuids: StoreUuids
  selectType: SelectType
  isOwner: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (event: 'update:modelValue', value: Role): void
  (event: 'update:storeUuids', value: StoreUuids): void
  (event: 'update:selectType', value: SelectType): void
  (event: 'handle-change-check-box'): void
}>()
const store = useStore()

const { t, tm } = useI18n()
const isFilterModal = ref(false)
const isStoreModal = ref(false)
const gmbSearchStoresLoading = computed(() => store.state.companiesGmbSearchStores.loading)
const searchTagGroupsLoading = computed(() => store.state.companiesSearchTagGroups.loading)
const initializedFormValues = computed(() => !!Object.keys(store.state.companiesSearchTagGroups.formValues).length)
const conditionTypeOptions = computed(() => ([
  {
    id: 'all',
    value: 'all',
    text: t('pages.companies.memberRoleEdit.selectType.all'),
  },
  {
    id: 'select',
    value: 'select',
    text: t('pages.companies.memberRoleEdit.selectType.select'),
  }
]))
const roles = computed<Role[]>(() => {
  const list = Object.values(roleConst)
  if (props.isOwner) {
    return list
  } else {
    return list.filter(v => v !== 'owner')
  }
})
const handleFilterSubmit = () => {
  isFilterModal.value = false
  isStoreModal.value = true
}
const handleFilterReset = () => {
  isFilterModal.value = false
}
const closeStoreModal = (uuids: string[]) => {
  isStoreModal.value = false
  emit('update:storeUuids', uuids)
}
const backFilterModal = () => {
  isStoreModal.value = false
  isFilterModal.value = true
}
</script>

<template>
  <KkBox class="w-full" border>
    <template #content>
      <div class="box">
        <KkFormLabel :text="t('components.KkRoleSelect.label')" />
        <div class="permissions">
          <div v-for="role in roles" :key="role" class="permissions__item">
            <div class="radio">
              <input
                :id="role"
                name="role"
                type="radio"
                class="radio__input"
                :checked="role === modelValue"
                :value="role"
                @input="emit('update:modelValue', role)"
              >
              <label :for="role" class="radio__label">
                {{ t(`components.KkRoleSelect.${role}.label`) }}
              </label>
            </div>
            <div>
              <p v-for="(text,key) in tm(`components.KkRoleSelect.${role}.description`)" :key="key">{{ text }}</p>
            </div>
            <KkBox v-if="modelValue === role && role !== 'owner'" border>
              <template #content>
                <p class="c-formLabel">{{ $t('labels.selectTargetStores') }}</p>
                <KkInfo icon="hint">
                  {{ $t('pages.companies.memberRoleEdit.selectType.info') }}
                </KkInfo>
                <KkRadioGroup
                  :model-value="selectType"
                  :items="conditionTypeOptions"
                  :name="'selectStoreType' + role"
                  @update:model-value="(e: SelectType) => emit('update:selectType', e)"
                />
                <template v-if="selectType === 'select'">
                  <SelectStore
                    v-if="!gmbSearchStoresLoading && !searchTagGroupsLoading && initializedFormValues"
                    :selected-store-count="storeUuids.length"
                    class="border"
                    @open-store-modal="isFilterModal = !isFilterModal"
                  />
                  <KkLoading v-else min-height="120px" />
                </template>
              </template>
            </KkBox>
          </div>
        </div>
      </div>
      <FilterModal
        v-if="isFilterModal"
        @close="isFilterModal = false"
        @submit="handleFilterSubmit"
        @reset="handleFilterReset"
        @change-check-box="emit('handle-change-check-box')"
      />
      <StoreModal
        v-if="isStoreModal"
        @close-store-modal="closeStoreModal"
        @back-filter-modal="backFilterModal"
      />
    </template>
  </KkBox>
</template>

<style scoped lang="scss">
@import "../../../../../../stylesheets/biz_spa/foundation/_var.scss";
.box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  font-size: 12px;
}
.permissions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  &__item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
.formItem {
  display: flex;
  align-items: center;
  gap: 8px;
}
.radio {
  padding: 0 12px 0 0;
  &__input {
    display: none;
    &:checked + .radio__label {
      &::before {
        background-color: $color-accent-form;
      }
    }
  }
  &__label {
    display: inline-block;
    position: relative;
    padding-left: 24px;
    font-weight: bold;
    font-size: 14px;
    line-height: 16px;
    cursor: pointer;
    transition: .2s;
    &::before {
      position: absolute;
      top: 0;
      left: 0;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background-color: $color-base-h;
      content: "";
      transition: .2s;
    }
    &::after {
      position: absolute;
      top: 5px;
      left: 4px;
      transform: rotate(-45deg);
      width: 8px;
      height: 4px;
      content: "";
      border-left: 2px solid #FFF;
      border-bottom: 2px solid #FFF;
    }
  }
}
.border {
  border: 1px solid #E4E7EB;
}
/* stylelint-disable selector-pseudo-class-no-unknown */
:deep(.radio) {
  display: grid;
}
:deep(.radio__item) {
  margin-bottom: 15px;
}
/* stylelint-enable */
</style>
